import React from "react";
import { useMediaQuery } from "@mui/material";
import { ContextMeshPlaceholder, StrengthWheel } from "./StrengthWheel";
import type { ContextMeshAnalysis } from "./contextMeshAnalysis";
import { useMeshStrengthData, type MeshStrengthStatus } from "./useMeshStrengthData";
import "../newProduct/tokens.css";

function statusLabel(analysis: ContextMeshAnalysis): string | null {
    const semantic = analysis.semanticAnalysis;
    if (semantic.resultSource === "last_ready") {
        if (semantic.status === "pending") return "Analysis updating · showing last completed score";
        if (semantic.status === "failed") return "Latest update failed · showing last completed score";
        return "Analysis outdated · showing last completed score";
    }
    const category = analysis.categoryAnalysis;
    if (category?.status === "failed") {
        return category.isStale && analysis.categoryScores ? "Latest category update failed · showing last scores" : "Category analysis unavailable";
    }
    if (category?.status === "pending") {
        return category.isStale && analysis.categoryScores ? "Category scores updating · showing last scores" : "Category scores analyzing";
    }
    return null;
}

function unavailableLabel(status: MeshStrengthStatus, code?: string): string {
    if (status === "analyzing") return "Context Mesh is being analyzed";
    return code === "BASELINE_INCOMPLETE" ? "Baseline needed" : "Analysis unavailable";
}

export function MeshStrengthEntryCard({
    projectId,
    onOpen,
    variant = "entry"
}: {
    projectId: number | null;
    onOpen: () => void;
    variant?: "entry" | "monitor";
}) {
    const compactLaptopLayout = useMediaQuery("(min-width: 1280px) and (min-height: 768px)");
    const tightLaptopHeight = useMediaQuery("(min-width: 1280px) and (min-height: 768px) and (max-height: 900px)");
    const monitorWheelSize = tightLaptopHeight ? 108 : compactLaptopLayout ? 132 : 104;
    const { analysis, status, loading, error } = useMeshStrengthData(projectId);
    if (projectId == null) return null;

    if (variant === "monitor") {
        const freshness = analysis ? statusLabel(analysis) : null;
        const categorySummary = [
            { key: "discover", label: "Discover Context", score: analysis?.categoryScores?.discover.score },
            { key: "external", label: "External Context", score: analysis?.categoryScores?.external.score },
            { key: "file", label: "File Context", score: analysis?.categoryScores?.file.score },
            { key: "code", label: "Code Context", score: analysis?.categoryScores?.code.score }
        ] as const;
        return (
            <div
                className="np-v6"
                data-testid="mesh-strength-monitor"
                style={{
                    width: "100%",
                    height: "100%",
                    maxWidth: "100%",
                    flex: "1 1 auto",
                    display: "grid",
                    gridTemplateColumns: `${tightLaptopHeight ? 114 : compactLaptopLayout ? 138 : 112}px minmax(0, 1fr)`,
                    gridTemplateRows: "auto 1fr auto",
                    alignItems: "center",
                    columnGap: compactLaptopLayout ? 10 : 16,
                    rowGap: compactLaptopLayout ? 4 : 6,
                    padding: "2px 0",
                    border: 0,
                    background: "transparent",
                    overflow: "hidden",
                    boxSizing: "border-box"
                }}
            >
                <span
                    style={{
                        gridColumn: "1 / -1",
                        width: "100%",
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: ".09em",
                        textTransform: "uppercase",
                        color: "var(--fg3)",
                        textAlign: "left"
                    }}
                >
                    Context Mesh
                </span>
                <span style={{ width: monitorWheelSize, height: monitorWheelSize, justifySelf: "center" }}>
                    {analysis ? (
                        <StrengthWheel
                            score={analysis.score}
                            tier={analysis.tier}
                            categoryScores={analysis.categoryScores}
                            uid={`monitor-${projectId}`}
                            showLabels={false}
                            hubScoreFontSize={tightLaptopHeight ? 60 : compactLaptopLayout ? 72 : 56}
                            hubGraphScale={0.75}
                            hubGraphYOffset={tightLaptopHeight ? -44 : compactLaptopLayout ? -58 : -42}
                        />
                    ) : (
                        <ContextMeshPlaceholder />
                    )}
                </span>
                {analysis && !loading && (
                    <span style={{ width: "100%", display: "grid", gap: 6 }}>
                        {categorySummary.map(({ key, label, score }) => (
                            <span
                                key={key}
                                data-testid={`mesh-strength-summary-${key}`}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: compactLaptopLayout ? 4 : 8,
                                    justifyContent: compactLaptopLayout ? "space-between" : undefined,
                                    paddingBottom: 5,
                                    borderBottom: "1px solid var(--divider)",
                                    fontSize: compactLaptopLayout ? 10.5 : 11.5
                                }}
                            >
                                <span style={{ minWidth: 0, flex: 1, color: "var(--fg1)", fontWeight: 600, whiteSpace: "nowrap" }}>{label}</span>
                                <span style={{ flex: "0 0 auto", fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--fg1)" }}>{score ?? "—"}</span>
                            </span>
                        ))}
                        {freshness && <span style={{ color: "#a16207", fontSize: 10.5, lineHeight: 1.35 }}>{freshness}</span>}
                    </span>
                )}
                {!analysis && !loading && <span style={{ width: "100%", color: "var(--fg3)", fontSize: 11.5, textAlign: "center" }}>{unavailableLabel(status, error?.code)}</span>}
                <button
                    type="button"
                    aria-label="Review Context Mesh"
                    onClick={onOpen}
                    data-ui-bridge="mesh-strength-entry"
                    data-ui-label="Review Context Mesh"
                    data-ui-role="button"
                    data-ui-group="feature-studio"
                    data-ui-description="Open the current product Context Mesh analysis"
                    style={{
                        gridColumn: "1 / -1",
                        width: "100%",
                        height: compactLaptopLayout ? 28 : 32,
                        minHeight: compactLaptopLayout ? 28 : 32,
                        display: "grid",
                        placeItems: "center",
                        border: "1px solid #6366f1",
                        borderRadius: 7,
                        fontSize: compactLaptopLayout ? 10.5 : 11.5,
                        fontWeight: 650,
                        color: "#4f46e5",
                        background: "transparent",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        boxSizing: "border-box"
                    }}
                >
                    Review/Enhance Context Mesh
                </button>
            </div>
        );
    }

    return (
        <div
            className="np-v6"
            data-testid="mesh-strength-entry"
            data-ui-bridge="mesh-strength-entry"
            onClick={onOpen}
            role="button"
            tabIndex={0}
            onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen();
                }
            }}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                margin: "8px 16px 0",
                padding: "8px 14px",
                borderRadius: 12,
                cursor: "pointer",
                background: "var(--bg-paper)",
                border: "1px solid var(--divider)",
                boxShadow: "var(--shadow-1)"
            }}
        >
            <span style={{ width: 44, height: 44, flex: "0 0 auto" }}>
                {analysis ? (
                    <StrengthWheel
                        score={analysis.score}
                        tier={analysis.tier}
                        categoryScores={analysis.categoryScores}
                        uid={`entry-${projectId}`}
                        showHub={false}
                        showLabels={false}
                    />
                ) : (
                    <ContextMeshPlaceholder showLabel={false} />
                )}
            </span>
            <span style={{ fontSize: 13, fontWeight: 650, color: "var(--fg1)" }}>Context Mesh</span>
            {analysis && !loading && (
                <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--font-mono)", color: "var(--fg1)" }}>{analysis.score}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--fg3)" }}>{analysis.tier}</span>
                </span>
            )}
            {!analysis && !loading && <span style={{ fontSize: 11.5, color: "var(--fg3)" }}>{unavailableLabel(status, error?.code)}</span>}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg3)" }}>View analysis →</span>
        </div>
    );
}
